import {all, call, put, takeLeading, takeLatest} from 'redux-saga/effects';

import {
  listAlerts,
  listSubscriptions,
  deleteAlert,
  deactivateSubscription,
  reactivateSubscription,
  deleteSubscription,
  upsertSubscription,
  createFromEvent,
  type UpsertSubPayload,
  type CreateFromEventPayload,
  type AlertItem,
  type SubscriptionItem,
} from '../../api/alerts';

import {
  fetchAlertsRequested,
  fetchAlertsSucceeded,
  fetchAlertsFailed,
  fetchSubsRequested,
  fetchSubsSucceeded,
  fetchSubsFailed,
  deleteAlertRequested,
  deleteAlertSucceeded,
  deleteAlertFailed,
  createFromEventRequested,
  createFromEventSucceeded,
  createFromEventFailed,
  deactivateSubRequested,
  deactivateSubSucceeded,
  deactivateSubFailed,
  reactivateSubRequested,
  reactivateSubSucceeded,
  reactivateSubFailed,
  deleteSubRequested,
  deleteSubSucceeded,
  deleteSubFailed,
  upsertSubRequested,
  upsertSubSucceeded,
  upsertSubFailed,
  createTickerSubsRequested,
  createTickerSubsSucceeded,
  createTickerSubsFailed,
} from '../slice/alertsSlice';

function normalizeApiError(error: any, fallbackMessage: string): string {
  return error?.response?.data?.message ?? error?.response?.data?.error ?? error?.message ?? fallbackMessage;
}

function* fetchAlertsWorker() {
  try {
    const data: AlertItem[] = yield call(listAlerts as any);
    yield put(fetchAlertsSucceeded(Array.isArray(data) ? data : []));
  } catch (error: any) {
    yield put(fetchAlertsFailed(normalizeApiError(error, '알림 목록 로드 실패')));
  }
}

function* fetchSubsWorker() {
  try {
    const data: SubscriptionItem[] = yield call(listSubscriptions as any);
    yield put(fetchSubsSucceeded(Array.isArray(data) ? data : []));
  } catch (error: any) {
    yield put(fetchSubsFailed(normalizeApiError(error, '구독 목록 로드 실패')));
  }
}

function* deleteAlertWorker(action: ReturnType<typeof deleteAlertRequested>) {
  const id = Number((action.payload as any)?.id ?? action.payload);
  try {
    if (!id || Number.isNaN(id)) throw new Error('잘못된 알림 ID');
    yield call(deleteAlert as any, id);
    yield put(deleteAlertSucceeded(id));
  } catch (error: any) {
    yield put(deleteAlertFailed({id, message: normalizeApiError(error, '알림 삭제 실패')}));
  }
}

function* createFromEventWorker(action: ReturnType<typeof createFromEventRequested>) {
  const raw = action.payload as Partial<
      CreateFromEventPayload & { userId?: string | number; telegramChatId?: string }
  >;
  const eventId = String(raw.eventId ?? '').trim();

  try {
    const payload: CreateFromEventPayload = {
      eventId,
      title: raw.title,
      isActive: typeof raw.isActive === 'boolean' ? raw.isActive : true,
      sector: raw.sector ?? null,
      channels: {telegram: true},
      filters: (raw as any).filters ?? null,
    };

    if (!payload.eventId) throw new Error('eventId 누락');

    yield call(createFromEvent as any, payload);
    yield put(createFromEventSucceeded({eventId}));
    yield call(fetchAlertsWorker);
  } catch (error: any) {
    yield put(
        createFromEventFailed({
          eventId,
          message: normalizeApiError(error, '알림 생성 실패'),
        }),
    );
  }
}

function* deactivateSubWorker(action: ReturnType<typeof deactivateSubRequested>) {
  const id = Number((action.payload as any)?.id ?? action.payload);
  try {
    if (!id || Number.isNaN(id)) throw new Error('잘못된 구독 ID');
    yield call(deactivateSubscription as any, id);
    yield put(deactivateSubSucceeded(id));
  } catch (error: any) {
    yield put(deactivateSubFailed({id, message: normalizeApiError(error, '구독 취소 실패')}));
  }
}

function* reactivateSubWorker(action: ReturnType<typeof reactivateSubRequested>) {
  const id = Number((action.payload as any)?.id ?? action.payload);
  try {
    if (!id || Number.isNaN(id)) throw new Error('잘못된 구독 ID');
    yield call(reactivateSubscription as any, id);
    yield put(reactivateSubSucceeded(id));
  } catch (error: any) {
    yield put(reactivateSubFailed({id, message: normalizeApiError(error, '구독 활성화 실패')}));
  }
}

function* deleteSubWorker(action: ReturnType<typeof deleteSubRequested>) {
  const id = Number((action.payload as any)?.id ?? action.payload);
  try {
    if (!id || Number.isNaN(id)) throw new Error('잘못된 구독 ID');
    yield call(deleteSubscription as any, id);
    yield put(deleteSubSucceeded(id));
  } catch (error: any) {
    yield put(deleteSubFailed({id, message: normalizeApiError(error, '구독 삭제 실패')}));
  }
}

function* upsertSubWorker(action: ReturnType<typeof upsertSubRequested>) {
  const raw = action.payload as any;
  const ticker = String(raw?.ticker ?? '').trim().toUpperCase();
  const channel = raw?.channel;
  const endpointId = String(raw?.endpointId ?? '').trim();

  try {
    if (!ticker) throw new Error('ticker 누락');
    if (channel !== 'telegram') throw new Error('현재는 텔레그램만 지원합니다.');
    if (!endpointId) throw new Error('텔레그램 Chat ID 누락');

    const payload: UpsertSubPayload = {
      ticker,
      channel: 'telegram',
      endpointId,
      isActive: true,
    };

    yield call(upsertSubscription as any, payload);
    yield put(upsertSubSucceeded({ticker, channel: 'telegram'}));
    yield call(fetchSubsWorker);
  } catch (error: any) {
    yield put(
        upsertSubFailed({
          ticker,
          channel: 'telegram',
          message: normalizeApiError(error, '구독 저장 실패'),
        }),
    );
  }
}

function* createTickerSubsWorker(action: ReturnType<typeof createTickerSubsRequested>) {
  const {tickers, channel, endpointId, sourceEventId} = action.payload as any;

  try {
    if (channel !== 'telegram') throw new Error('현재는 텔레그램만 지원합니다.');

    const chatId = String(endpointId ?? '').trim();
    if (!chatId) throw new Error('텔레그램 Chat ID가 필요합니다.');

    const tickerList: string[] = Array.isArray(tickers)
        ? Array.from(
            new Set(
                tickers
                .map((ticker) => String(ticker ?? '').trim().toUpperCase())
                .filter(Boolean),
            ),
        )
        : [];

    if (!tickerList.length) throw new Error('tickers 비어있음');

    for (const ticker of tickerList) {
      const payload: UpsertSubPayload = {
        ticker,
        channel: 'telegram',
        endpointId: chatId,
        isActive: true,
      };
      yield call(upsertSubscription as any, payload);
    }

    yield put(createTickerSubsSucceeded({sourceEventId, channel: 'telegram'}));
    yield call(fetchSubsWorker);
  } catch (error: any) {
    yield put(
        createTickerSubsFailed({
          sourceEventId,
          channel: 'telegram',
          message: normalizeApiError(error, '선택 종목 구독 생성 실패'),
        }),
    );
  }
}

function* watchFetchAlerts() {
  yield takeLeading(fetchAlertsRequested.type, fetchAlertsWorker);
}

function* watchFetchSubs() {
  yield takeLeading(fetchSubsRequested.type, fetchSubsWorker);
}

function* watchDeleteAlert() {
  yield takeLatest(deleteAlertRequested.type, deleteAlertWorker);
}

function* watchCreateFromEvent() {
  yield takeLatest(createFromEventRequested.type, createFromEventWorker);
}

function* watchDeactivateSub() {
  yield takeLatest(deactivateSubRequested.type, deactivateSubWorker);
}

function* watchReactivateSub() {
  yield takeLatest(reactivateSubRequested.type, reactivateSubWorker);
}

function* watchDeleteSub() {
  yield takeLatest(deleteSubRequested.type, deleteSubWorker);
}

function* watchUpsertSub() {
  yield takeLatest(upsertSubRequested.type, upsertSubWorker);
}

function* watchCreateTickerSubs() {
  yield takeLatest(createTickerSubsRequested.type, createTickerSubsWorker);
}

export default function* alertsSaga() {
  yield all([
    watchFetchAlerts(),
    watchFetchSubs(),
    watchDeleteAlert(),
    watchCreateFromEvent(),
    watchDeactivateSub(),
    watchReactivateSub(),
    watchDeleteSub(),
    watchUpsertSub(),
    watchCreateTickerSubs(),
  ]);
}
